import type { FieldValues } from 'react-hook-form';
import { sentryCaptureError } from 'services/sentry/capture';
import type { Option, Parameter } from 'store/models';
import {
  CurrencyDisplaySetting,
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType as ExtendedTypes,
  PayHubAdminPanelDomainEnumsParameterDataType,
  PayHubEnumsCashierMethodParamType,
} from 'store/models';
import type { CardState } from 'store/slices/userCard';
import { getIsRtl, getPattern, getSelectedOption, isEmpty } from 'utils';

import type { DynamicTypeObject } from '../commonTypes';
const normalizeParamWithSpaces = (field: string) => {
  const trimmedName = field.trim();
  return trimmedName.replace(/\s+/g, ' ');
};
import { FlowForPrepareExpDate, getExpirationObject } from '../store/formatters';
import { normalizedStringValue } from './math/math';

export const setNullToNotRequiredFields = (
  requiredFields: Parameter[],
  fields: DynamicTypeObject,
  dirtyFields: Partial<Record<keyof FieldValues, boolean>> = {},
): DynamicTypeObject => {
  const preparedFields: DynamicTypeObject = {};
  const requiredFieldList = requiredFields.reduce((acc: DynamicTypeObject, { name, isRequired }) => {
    acc[name] = isRequired;

    return acc;
  }, {});
  // Mobile decimal keyboards in some locales type `,` — the backend expects `.` for Float values.
  const floatFieldNames = requiredFields
    .filter(({ dataType }) => dataType === PayHubAdminPanelDomainEnumsParameterDataType.Float)
    .map(({ name }) => name);

  const entries = Object.entries(fields);
  entries.forEach(([key, value]) => {
    if (key === 'amount') {
      preparedFields.amount = value;
      return;
    }

    if (!requiredFieldList[key] && !['number', 'boolean'].includes(typeof value) && !value) {
      if (dirtyFields[key]) {
        preparedFields[key] = null;
      } else {
        delete preparedFields[key];
      }
    } else if (typeof value === 'string' && floatFieldNames.includes(key)) {
      preparedFields[key] = value.replace(',', '.');
    } else {
      preparedFields[key] = value;
    }
  });

  return preparedFields;
};

interface AdditionalParams {
  isCvvPresent?: boolean;
  isCardTokenActive?: boolean;
  isCheckout?: boolean;
  isWithdrawal?: boolean;
  fieldExpDateYearName?: string;
  fieldExpDateMonthName?: string;
}
export const prepareParametersForSubmit = (
  fields: FieldValues,
  additionalParams: AdditionalParams,
  isNew?: boolean,
) => {
  const data = {
    ...fields,
  };
  const {
    isCardTokenActive = false,
    isCheckout = false,
    isWithdrawal = false,
    fieldExpDateYearName = '',
    fieldExpDateMonthName = '',
  } = additionalParams;

  if (fields.expirationDate) {
    let flowType;

    switch (true) {
      case isWithdrawal:
        flowType = FlowForPrepareExpDate.WITHDRAWAL;
        break;

      case isCheckout:
        flowType = FlowForPrepareExpDate.CHECKOUT;
        break;

      default:
        flowType = FlowForPrepareExpDate.DEPOSIT;
        break;
    }

    const expirationDataObj = getExpirationObject(
      fields.expirationDate,
      fieldExpDateYearName,
      fieldExpDateMonthName,
      flowType,
    );
    delete data.expirationDate;
    Object.assign(data, expirationDataObj);
  }

  if (data.card_id && !isNew) {
    delete data.card_pan;
  }

  if (isCheckout && !isNew) {
    data.is_card_token_active = isCardTokenActive;
  }

  if (fields.card_cardholder_name) {
    data.card_cardholder_name = normalizeParamWithSpaces(data.card_cardholder_name);

    if (!data.card_cardholder_name) {
      delete data.card_cardholder_name;
    }
  }

  const result: Record<string, string | number | unknown> = {};

  Object.keys(data).forEach((item) => {
    const fieldValue: string | number | unknown | Option = fields[item];
    if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      result[item] = fieldValue.value;
    } else {
      result[item] = fieldValue;
    }
  });

  return data;
};

const sortFieldsByDefinedOrder = (arr: Parameter[], orderedArr: ExtendedTypes[]) =>
  arr.sort((a, b) => {
    const isAFileUpload = a?.type === PayHubEnumsCashierMethodParamType.FileUpload;
    const isBFileUpload = b?.type === PayHubEnumsCashierMethodParamType.FileUpload;

    if (isAFileUpload && !isBFileUpload) {
      return 1;
    }
    if (!isAFileUpload && isBFileUpload) {
      return -1;
    }

    const aIndex = orderedArr.indexOf(a.extendedType as ExtendedTypes);
    const bIndex = orderedArr.indexOf(b.extendedType as ExtendedTypes);

    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });

export const prepareFieldsWithCustomParameters = (fields: Parameter[], cardInfo?: CardState): Parameter[] => {
  const SKIPPED_FIELDS = [ExtendedTypes.CardExpYear, ExtendedTypes.CardExpMonth, ExtendedTypes.CardCvv];

  const orderedTypes = [ExtendedTypes.CardPan, ExtendedTypes.Custom, ExtendedTypes.CardCardholderName];

  const pairParameter: Parameter = {
    name: 'pair',
    key: 'pair',
    type: 'pair',
    extendedType: ExtendedTypes.Custom,
    dataType: PayHubAdminPanelDomainEnumsParameterDataType.String,
    items: [],
    customRegExp: {},
  };

  const fieldMonth = fields.find(({ extendedType }) => extendedType === ExtendedTypes.CardExpMonth);
  const fieldYear = fields.find(({ extendedType }) => extendedType === ExtendedTypes.CardExpYear);
  const fieldCVV = fields.find(({ extendedType }) => extendedType === ExtendedTypes.CardCvv);

  if (fieldMonth && fieldYear) {
    const customParameter: Parameter = {
      extendedType: ExtendedTypes.Custom,
      name: 'expirationDate',
      key: 'expirationDate',
      type: fieldMonth.type,
      dataType: fieldMonth.dataType,
      isRequired: fieldMonth.isRequired || fieldYear.isRequired,
      validationKeys: {
        month: fieldMonth.validationMsgKey,
        year: fieldYear.validationMsgKey,
      },
    };

    const patternMonth = getPattern(fieldMonth.regexp);
    if (patternMonth) {
      customParameter.customRegExp = {
        month: patternMonth,
      };
    }

    const patternYear = getPattern(fieldYear.regexp);
    if (patternYear) {
      customParameter.customRegExp = {
        ...customParameter.customRegExp,
        year: patternYear,
      };
    }
    pairParameter.items?.push(customParameter);
  }

  const shouldHasCVV = fieldCVV || cardInfo?.selectedCard?.isNew || !cardInfo?.selectedCard?.is_card_token_active;
  const isCardExist = cardInfo && cardInfo.selectedCard;
  const isNewCard = !isCardExist || isEmpty(cardInfo.selectedCard) || cardInfo.selectedCard.isNew;

  if (!isNewCard) {
    SKIPPED_FIELDS.push(ExtendedTypes.SaveRequisites);
  }

  const PAIR_PARAMETER_TYPES = shouldHasCVV ? [ExtendedTypes.Custom, ExtendedTypes.CardCvv] : [ExtendedTypes.Custom];

  fields.forEach((field: Parameter) => {
    if (field.extendedType && PAIR_PARAMETER_TYPES.includes(field.extendedType)) {
      pairParameter.items?.push(field);
    }
  });

  const updatedFields = [...fields];

  if (pairParameter.items?.length) {
    updatedFields.push(pairParameter);
  }

  const filtered = updatedFields.filter(({ extendedType }) => !SKIPPED_FIELDS.includes(extendedType as ExtendedTypes));

  return sortFieldsByDefinedOrder(filtered, orderedTypes);
};

export const formatMethodName = (methodName?: string) => {
  if (!methodName) {
    return '';
  }

  return methodName.trim().toLowerCase().replace(/ /gi, '_');
};

export const stringToInt = (value: string, radix = 10) => parseInt(value, radix);

export const getFormDefaultValues = (fields: Parameter[], betShopCheckboxField?: FieldValues): FieldValues => {
  const defaultValues: { [key: string]: string | number | boolean | Option } = {};
  const typeDefaultValues: { [key: string]: string | number | boolean } = {
    string: '',
    number: 0,
    boolean: false,
  };

  if (!fields || !fields.length) {
    return defaultValues;
  }

  fields.forEach((field) => {
    const dataType = field.dataType?.toLowerCase();
    const selectedOption = getSelectedOption(field);

    defaultValues[field.name] = field.userRequisite || typeDefaultValues[dataType] || selectedOption || '';
  });

  if (betShopCheckboxField) {
    defaultValues[betShopCheckboxField.name] = true;
  }

  return defaultValues;
};

// https://github.com/bengourley/currency-symbol-map
const currencySymbolMapping: { [key: string]: string } = {
  AED: 'د.إ',
  AFN: '؋',
  ALL: 'L',
  AMD: '֏',
  ANG: 'ƒ',
  AOA: 'Kz',
  ARS: '$',
  AUD: '$',
  AWG: 'ƒ',
  AZN: '₼',
  BAM: 'KM',
  BBD: '$',
  BDT: '৳',
  BGN: 'лв',
  BHD: '.د.ب',
  BIF: 'FBu',
  BMD: '$',
  BND: '$',
  BOB: '$b',
  BRL: 'R$',
  BSD: '$',
  BTC: '₿',
  BTN: 'Nu.',
  BWP: 'P',
  BZD: 'BZ$',
  CAD: '$',
  CDF: 'FC',
  CHF: 'CHF',
  CLP: 'CLP',
  CNH: '¥',
  CNY: '¥',
  COP: '$',
  CRC: '₡',
  CUC: '$',
  CUP: '₱',
  CVE: '$',
  CZK: 'Kč',
  DJF: 'Fdj',
  DKK: 'kr',
  DOP: 'RD$',
  DZD: 'دج',
  EGP: '£',
  ERN: 'Nfk',
  ETB: 'Br',
  ETH: 'Ξ',
  EUR: '€',
  FJD: '$',
  FKP: '£',
  GBP: '£',
  GEL: '₾',
  GGP: '£',
  GHS: 'GH₵',
  GIP: '£',
  GMD: 'D',
  GNF: 'FG',
  GTQ: 'Q',
  GYD: '$',
  HKD: '$',
  HNL: 'L',
  HTG: 'G',
  HUF: 'Ft',
  IDR: 'Rp',
  ILS: '₪',
  IMP: '£',
  INR: '₹',
  IQD: 'ع.д',
  IRR: '﷼',
  ISK: 'kr',
  JEP: '£',
  JMD: 'J$',
  JOD: 'JD',
  JPY: '¥',
  KES: 'KSh',
  KGS: 'с',
  KHR: '៛',
  KMF: 'CF',
  KPW: '₩',
  KRW: '₩',
  KWD: 'KD',
  KYD: '$',
  KZT: '₸',
  LAK: '₭',
  LBP: '£',
  LKR: '₨',
  LRD: '$',
  LSL: 'M',
  LTC: 'Ł',
  LYD: 'LD',
  MAD: 'د.م.',
  MDL: 'lei',
  MGA: 'Ar',
  MKD: 'ден',
  MMK: 'K',
  MNT: '₮',
  MOP: 'MOP$',
  MRU: 'UM',
  MUR: '₨',
  MVR: 'Rf',
  MWK: 'MK',
  MXN: '$',
  MYR: 'RM',
  MZN: 'MT',
  NAD: '$',
  NGN: '₦',
  NIO: 'C$',
  NOK: 'kr',
  NPR: '₨',
  NZD: '$',
  OMR: '﷼',
  PAB: 'B/.',
  PEN: 'S/',
  PGK: 'K',
  PHP: '₱',
  PKR: '₨',
  PLN: 'zł',
  PYG: 'Gs',
  QAR: '﷼',
  RMB: '￥',
  RON: 'lei',
  RSD: 'Дин.',
  RWF: 'R₣',
  SAR: '﷼',
  SBD: '$',
  SCR: '₨',
  SDG: 'ج.с.',
  SEK: 'kr',
  SGD: 'S$',
  SHP: '£',
  SLL: 'Le',
  SOS: 'S',
  SRD: '$',
  SSP: '£',
  STN: 'Db',
  SVC: '$',
  SYP: '£',
  SZL: 'E',
  THB: '฿',
  TJS: 'SM',
  TMT: 'T',
  TND: 'د.т',
  TOP: 'T$',
  TRY: '₺',
  TTD: 'TT$',
  TVD: '$',
  TWD: 'NT$',
  TZS: 'TSh',
  UAH: '₴',
  UGX: 'USh',
  USD: '$',
  UYU: '$U',
  UZS: 'сум',
  VEF: 'Bs',
  VES: 'Bs.S',
  VND: '₫',
  VUV: 'VT',
  WST: 'WS$',
  XAF: 'FCFA',
  XBT: 'Ƀ',
  XCD: '$',
  XOF: '₣',
  XPF: '₣',
  YER: '﷼',
  ZAR: 'R',
  ZMW: 'ZK',
  ZWD: 'Z$',
  ZWL: '$',
};
const SYMBOL_CURRENCY = [CurrencyDisplaySetting.digitsSymbol, CurrencyDisplaySetting.symbolDigits];

export const getSymbolFromCurrency = (
  value: string,
  currencyDisplaySetting: CurrencyDisplaySetting = CurrencyDisplaySetting.digitsIso,
) => {
  if (SYMBOL_CURRENCY.includes(currencyDisplaySetting)) {
    return currencySymbolMapping[value] || value.toUpperCase();
  }
  return value.toUpperCase().trim();
};

/*
 *
 * "1" → "Deposit 1 000.50 USD"
 * "2" → "Deposit USD 1 000.50"
 * "3" → "Deposit 1 000.50 $"
 * "4" → "Deposit $ 1 000.50"
 *
 * */
const DIGITS_CURRENCY_IN_ORDER = [
  CurrencyDisplaySetting.isoDigits, // 2
  CurrencyDisplaySetting.symbolDigits, // 4
];

export const formatAmountInShortView = (amount: string) => {
  if (!amount) {
    return amount;
  }

  try {
    const cleanAmount = Number(amount);
    const num = Number(cleanAmount);

    if (Number.isNaN(num) || num < 1000) {
      return amount;
    }

    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumSignificantDigits: 3,
    }).format(num);
  } catch (e) {
    sentryCaptureError(e);
    return amount;
  }
};

export const getPrettierFormatNumber = (value?: number | string) => {
  if (value === undefined || value === null) {
    return '';
  }
  const stringValue = value.toString();
  const normalizedVal = normalizedStringValue(stringValue);
  const [intPart = '', decPart = ''] = normalizedVal.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');

  if (decPart) return `${formattedInt}.${decPart}`;

  return `${formattedInt}`;
};

export const getFormattedAmountWithCurrency = (
  amount: string | number,
  currency = '',
  currencyDisplaySetting: CurrencyDisplaySetting = CurrencyDisplaySetting.digitsIso,
  isShortAmountView = false,
  isSumAmountView = false,
) => {
  const isRtl = getIsRtl();
  const currencySymbol = getSymbolFromCurrency(currency, currencyDisplaySetting);
  const formatedAmount = isShortAmountView ? formatAmountInShortView(`${amount}`) : getPrettierFormatNumber(amount);
  const plusSymbol = isSumAmountView ? '+' : '';

  // 2, 4 variants (before number)
  if (DIGITS_CURRENCY_IN_ORDER.includes(currencyDisplaySetting)) {
    return isRtl
      ? `${formatedAmount}\u00A0${currencySymbol}${plusSymbol}`
      : `${plusSymbol}${currencySymbol} ${formatedAmount}`;
  }

  // 1, 3 variants (after number)
  return isRtl
    ? `${currencySymbol}\u2067\u202F${formatedAmount}${plusSymbol}`
    : `${plusSymbol}${formatedAmount} ${currencySymbol}`;
};

export const getStringWithoutSpaces = (value: string) => value.replace(/\s/g, '');

export const fromNumberToStringForFloat = (value: number | string, coefficient = 4): string => {
  const num = Number(value);

  if (Number.isNaN(num)) {
    return '0';
  }

  const frac = Math.abs(num) % 1;
  const scaled = Math.floor(frac * 10 ** coefficient);

  return String(scaled).padStart(coefficient, '0');
};
